import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Bell, CheckCircle, XCircle, MapPin, Phone, Mail,
    Calendar, Clock, Home, Building2, FileText,
    Camera, ChevronDown, ChevronUp, ShoppingBag, RefreshCw,
    AlertCircle, ClipboardList, Check, X, StickyNote, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { useAuth } from '../App';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';

const SalesmanAssignments = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [rejectModal, setRejectModal] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [notes, setNotes] = useState({});
    const [uploadingProof, setUploadingProof] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [submitting, setSubmitting] = useState(false);
    const proofInputRef = useRef(null);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchAssignments = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/salesman/assignments`, { headers });
            setAssignments(res.data);
            const notesMap = {};
            res.data.forEach(a => { if (a.salesman_notes) notesMap[a.id] = a.salesman_notes; });
            setNotes(notesMap);
        } catch (e) {
            toast.error('Failed to load assignments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAssignments(); }, []);

    const handleAccept = async (visitId) => {
        setSubmitting(true);
        try {
            await axios.post(`${API}/api/salesman/assignments/${visitId}/accept`, {}, { headers });
            toast.success('Assignment accepted! Customer has been notified.');
            fetchAssignments();
        } catch (e) {
            toast.error('Failed to accept assignment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) return toast.error('Please provide a reason');
        setSubmitting(true);
        try {
            await axios.post(`${API}/api/salesman/assignments/${rejectModal}/reject`, { reason: rejectReason }, { headers });
            toast.success('Assignment rejected');
            setRejectModal(null);
            setRejectReason('');
            fetchAssignments();
        } catch (e) {
            toast.error('Failed to reject assignment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveNotes = async (visitId) => {
        try {
            await axios.post(`${API}/api/salesman/assignments/${visitId}/notes`, { notes: notes[visitId] || '' }, { headers });
            toast.success('Notes saved');
        } catch (e) {
            toast.error('Failed to save notes');
        }
    };

    const handleProofUpload = async (visitId, file) => {
        if (!file) return;
        setUploadingProof(visitId);
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`${API}/api/salesman/assignments/${visitId}/proof`, formData, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Visit proof uploaded! Visit marked as completed.');
            fetchAssignments();
        } catch (e) {
            toast.error('Failed to upload proof');
        } finally {
            setUploadingProof(null);
        }
    };

    const statusConfig = {
        pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30', icon: Clock },
        scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30', icon: Calendar },
        in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30', icon: ClipboardList },
        completed: { label: 'Completed', color: 'bg-green-100 text-green-700 dark:bg-green-900/30', icon: CheckCircle },
        cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 dark:bg-red-900/30', icon: XCircle },
    };

    const getAcceptanceBadge = (a) => {
        if (a.salesman_accepted === true) return <span className="flex items-center gap-1 text-xs text-green-600 font-medium"><Check className="w-3 h-3" />Accepted</span>;
        if (a.salesman_accepted === false) return <span className="flex items-center gap-1 text-xs text-red-600 font-medium"><X className="w-3 h-3" />Rejected</span>;
        return <span className="flex items-center gap-1 text-xs text-yellow-600 font-medium"><AlertCircle className="w-3 h-3" />Awaiting Response</span>;
    };

    const filtered = filterStatus === 'all' ? assignments : assignments.filter(a => a.status === filterStatus);
    const newCount = assignments.filter(a => a.salesman_accepted === null || a.salesman_accepted === undefined).length;

    return (
        <DashboardLayout userRole="salesman">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Bell className="w-7 h-7 text-primary" />
                            My Assignments
                            {newCount > 0 && (
                                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{newCount} new</span>
                            )}
                        </h1>
                        <p className="text-muted-foreground text-sm mt-1">Expert visits assigned to you by admin</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={fetchAssignments}><RefreshCw className="w-4 h-4" /></Button>
                        <Button className="btn-primary gap-2" onClick={() => navigate('/salesman/place-order')}>
                            <ShoppingBag className="w-4 h-4" />
                            Place Order
                        </Button>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-1">
                    {[
                        { key: 'all', label: `All (${assignments.length})` },
                        { key: 'pending', label: 'Pending' },
                        { key: 'scheduled', label: 'Scheduled' },
                        { key: 'completed', label: 'Completed' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setFilterStatus(tab.key)}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                                filterStatus === tab.key
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Assignments List */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20 text-muted-foreground bg-card border rounded-xl">
                        <ClipboardList className="w-14 h-14 mx-auto mb-3 opacity-20" />
                        <p className="font-medium">No assignments found</p>
                        <p className="text-sm">Admin will assign expert visit requests to you</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filtered.map(a => {
                            const sc = statusConfig[a.status] || statusConfig.pending;
                            const StatusIcon = sc.icon;
                            const isExpanded = expanded === a.id;
                            const isNew = a.salesman_accepted === null || a.salesman_accepted === undefined;
                            return (
                                <div key={a.id} className={`bg-card border rounded-xl overflow-hidden transition-all duration-200 ${isNew ? 'border-yellow-400 dark:border-yellow-600 shadow-lg shadow-yellow-500/10' : ''}`}>
                                    {/* Card Header */}
                                    <div className="p-5">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-bold text-base">{a.name}</h3>
                                                    {isNew && <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full dark:bg-yellow-900/30">NEW</span>}
                                                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${sc.color}`}>
                                                        <StatusIcon className="w-3 h-3" />
                                                        {sc.label}
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                                                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{a.address}, {a.city} – {a.pincode}</span>
                                                    <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{a.phone}</span>
                                                    {a.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{a.email}</span>}
                                                </div>
                                                <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                                                    {a.preferred_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{a.preferred_date}</span>}
                                                    {a.preferred_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.preferred_time}</span>}
                                                    <span className="flex items-center gap-1">
                                                        {a.property_type === 'home' ? <Home className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                                                        {a.property_type}
                                                    </span>
                                                    {getAcceptanceBadge(a)}
                                                </div>
                                            </div>
                                            <button onClick={() => setExpanded(isExpanded ? null : a.id)} className="text-muted-foreground hover:text-foreground p-1">
                                                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                            </button>
                                        </div>

                                        {/* Action Buttons - shown when new */}
                                        {isNew && (
                                            <div className="flex gap-3 mt-4">
                                                <Button
                                                    className="flex-1 gap-2 bg-green-600 hover:bg-green-700 text-white"
                                                    onClick={() => handleAccept(a.id)}
                                                    disabled={submitting}
                                                >
                                                    <CheckCircle className="w-4 h-4" />
                                                    Accept Assignment
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                    onClick={() => setRejectModal(a.id)}
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                    Reject
                                                </Button>
                                            </div>
                                        )}

                                        {/* Action Buttons - accepted/scheduled */}
                                        {a.salesman_accepted === true && a.status !== 'completed' && (
                                            <div className="flex gap-3 mt-4">
                                                <Button
                                                    className="flex-1 gap-2 btn-primary"
                                                    onClick={() => navigate(`/salesman/place-order?visitId=${a.id}&customerName=${encodeURIComponent(a.name)}&customerPhone=${encodeURIComponent(a.phone)}&customerEmail=${encodeURIComponent(a.email || '')}&address=${encodeURIComponent(a.address + ', ' + a.city)}`)}
                                                >
                                                    <ShoppingBag className="w-4 h-4" />
                                                    Place Order for Customer
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Expanded Section */}
                                    {isExpanded && (
                                        <div className="border-t bg-muted/20 p-5 space-y-5">
                                            {/* Customer Details */}
                                            <div className="grid md:grid-cols-2 gap-4">
                                                {a.monthly_bill && (
                                                    <div className="bg-background border rounded-lg p-3">
                                                        <p className="text-xs text-muted-foreground">Monthly Bill</p>
                                                        <p className="font-semibold">₹{a.monthly_bill?.toLocaleString('en-IN')}</p>
                                                    </div>
                                                )}
                                                {a.roof_area_sqft && (
                                                    <div className="bg-background border rounded-lg p-3">
                                                        <p className="text-xs text-muted-foreground">Roof Area</p>
                                                        <p className="font-semibold">{a.roof_area_sqft} sq.ft.</p>
                                                    </div>
                                                )}
                                                {a.admin_notes && (
                                                    <div className="bg-background border rounded-lg p-3 md:col-span-2">
                                                        <p className="text-xs text-muted-foreground mb-1">Admin Notes</p>
                                                        <p className="text-sm">{a.admin_notes}</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Salesman Notes */}
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium flex items-center gap-2">
                                                    <StickyNote className="w-4 h-4 text-primary" />
                                                    My Visit Notes
                                                </label>
                                                <textarea
                                                    className="w-full border rounded-lg px-3 py-2 text-sm bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                                                    rows={3}
                                                    placeholder="Add notes about this visit..."
                                                    value={notes[a.id] || ''}
                                                    onChange={e => setNotes(prev => ({ ...prev, [a.id]: e.target.value }))}
                                                />
                                                <Button size="sm" variant="outline" onClick={() => handleSaveNotes(a.id)}>
                                                    Save Notes
                                                </Button>
                                            </div>

                                            {/* Proof Upload */}
                                            {a.salesman_accepted === true && (
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium flex items-center gap-2">
                                                        <Camera className="w-4 h-4 text-primary" />
                                                        Visit Proof Photo
                                                    </label>
                                                    {a.visit_proof_url ? (
                                                        <div className="flex items-center gap-3">
                                                            <img
                                                                src={`${API}${a.visit_proof_url}`}
                                                                alt="Visit proof"
                                                                className="w-24 h-24 object-cover rounded-lg border"
                                                            />
                                                            <div>
                                                                <p className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Proof uploaded</p>
                                                                <p className="text-xs text-muted-foreground mt-0.5">Visit marked as completed</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <input
                                                                ref={proofInputRef}
                                                                type="file"
                                                                accept="image/*,.pdf"
                                                                className="hidden"
                                                                onChange={e => handleProofUpload(a.id, e.target.files[0])}
                                                            />
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="gap-2"
                                                                onClick={() => { proofInputRef.current.click(); }}
                                                                disabled={uploadingProof === a.id}
                                                            >
                                                                <Upload className="w-4 h-4" />
                                                                {uploadingProof === a.id ? 'Uploading...' : 'Upload Proof Photo'}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Reject Modal */}
            {rejectModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm border p-6">
                        <h3 className="text-lg font-bold mb-2">Reject Assignment</h3>
                        <p className="text-sm text-muted-foreground mb-4">Please provide a reason. Admin will be notified and may reassign.</p>
                        <textarea
                            className="w-full border rounded-lg px-3 py-2 text-sm bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 mb-4"
                            rows={3}
                            placeholder="e.g., Schedule conflict, customer not reachable..."
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                        />
                        <div className="flex gap-3">
                            <Button variant="outline" className="flex-1" onClick={() => { setRejectModal(null); setRejectReason(''); }}>Cancel</Button>
                            <Button className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleReject} disabled={submitting}>
                                {submitting ? 'Rejecting...' : 'Reject'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default SalesmanAssignments;
